import React, { useRef, useEffect } from 'react';

// Reusable Shader Background Hook
export const useShaderBackground = () => {
    const canvasRef = useRef(null);
    const animationFrameRef = useRef();
    const rendererRef = useRef(null);
    const pointersRef = useRef(null);

    // WebGL Renderer class
    class WebGLRenderer {
        constructor(canvas, scale) {
            this.canvas = canvas;
            this.scale = scale;
            this.gl = canvas.getContext('webgl2');
            this.gl.viewport(0, 0, canvas.width * scale, canvas.height * scale);
            this.shaderSource = defaultShaderSource;
            this.mouseMove = [0, 0];
            this.mouseCoords = [0, 0];
            this.pointerCoords = [0, 0];
            this.nbrOfPointers = 0;
            this.program = null;
            this.vs = null;
            this.fs = null;
            this.buffer = null;

            this.vertexSrc = `#version 300 es
precision highp float;
in vec4 position;
void main(){gl_Position=position;}`;

            this.vertices = [-1, 1, -1, -1, 1, 1, 1, -1];
        }

        updateShader(source) {
            this.reset();
            this.shaderSource = source;
            this.setup();
            this.init();
        }

        updateMove(deltas) {
            this.mouseMove = deltas;
        }

        updateMouse(coords) {
            this.mouseCoords = coords;
        }

        updatePointerCoords(coords) {
            this.pointerCoords = coords;
        }

        updatePointerCount(nbr) {
            this.nbrOfPointers = nbr;
        }

        updateScale(scale) {
            this.scale = scale;
            this.gl.viewport(0, 0, this.canvas.width * scale, this.canvas.height * scale);
        }

        compile(shader, source) {
            const gl = this.gl;
            gl.shaderSource(shader, source);
            gl.compileShader(shader);

            if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
                const error = gl.getShaderInfoLog(shader);
                console.error('Shader compilation error:', error);
            }
        }

        test(source) {
            let result = null;
            const gl = this.gl;
            const shader = gl.createShader(gl.FRAGMENT_SHADER);
            gl.shaderSource(shader, source);
            gl.compileShader(shader);

            if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
                result = gl.getShaderInfoLog(shader);
            }
            gl.deleteShader(shader);
            return result;
        }

        reset() {
            const gl = this.gl;
            if (this.program && !gl.getProgramParameter(this.program, gl.DELETE_STATUS)) {
                if (this.vs) {
                    gl.detachShader(this.program, this.vs);
                    gl.deleteShader(this.vs);
                }
                if (this.fs) {
                    gl.detachShader(this.program, this.fs);
                    gl.deleteShader(this.fs);
                }
                gl.deleteProgram(this.program);
            }
        }

        setup() {
            const gl = this.gl;
            this.vs = gl.createShader(gl.VERTEX_SHADER);
            this.fs = gl.createShader(gl.FRAGMENT_SHADER);
            this.compile(this.vs, this.vertexSrc);
            this.compile(this.fs, this.shaderSource);
            this.program = gl.createProgram();
            gl.attachShader(this.program, this.vs);
            gl.attachShader(this.program, this.fs);
            gl.linkProgram(this.program);

            if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) {
                console.error(gl.getProgramInfoLog(this.program));
            }
        }

        init() {
            const gl = this.gl;
            const program = this.program;

            this.buffer = gl.createBuffer();
            gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(this.vertices), gl.STATIC_DRAW);

            const position = gl.getAttribLocation(program, 'position');
            gl.enableVertexAttribArray(position);
            gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

            program.resolution = gl.getUniformLocation(program, 'resolution');
            program.time = gl.getUniformLocation(program, 'time');
            program.move = gl.getUniformLocation(program, 'move');
            program.touch = gl.getUniformLocation(program, 'touch');
            program.pointerCount = gl.getUniformLocation(program, 'pointerCount');
            program.pointers = gl.getUniformLocation(program, 'pointers');
        }

        render(now = 0) {
            const gl = this.gl;
            const program = this.program;

            if (!program || gl.getProgramParameter(program, gl.DELETE_STATUS)) return;

            gl.clearColor(0, 0, 0, 1);
            gl.clear(gl.COLOR_BUFFER_BIT);
            gl.useProgram(program);
            gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);

            gl.uniform2f(program.resolution, this.canvas.width, this.canvas.height);
            gl.uniform1f(program.time, now * 1e-3);
            gl.uniform2f(program.move, ...this.mouseMove);
            gl.uniform2f(program.touch, ...this.mouseCoords);
            gl.uniform1i(program.pointerCount, this.nbrOfPointers);

            if (this.pointerCoords && this.pointerCoords.length > 0) {
                gl.uniform2fv(program.pointers, this.pointerCoords);
            }

            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        }
    }

    // Pointer Handler class
    class PointerHandler {
        constructor(element, scale) {
            this.scale = scale;
            this.active = false;
            this.pointers = new Map();
            this.lastCoords = [0, 0];
            this.moves = [0, 0];

            const map = (el, scl, x, y) =>
                [x * scl, el.height - y * scl];

            element.addEventListener('pointerdown', (e) => {
                this.active = true;
                this.pointers.set(e.pointerId, map(element, this.getScale(), e.clientX, e.clientY));
            });

            element.addEventListener('pointerup', (e) => {
                if (this.count === 1) {
                    this.lastCoords = this.first;
                }
                this.pointers.delete(e.pointerId);
                this.active = this.pointers.size > 0;
            });

            element.addEventListener('pointerleave', (e) => {
                if (this.count === 1) {
                    this.lastCoords = this.first;
                }
                this.pointers.delete(e.pointerId);
                this.active = this.pointers.size > 0;
            });

            element.addEventListener('pointermove', (e) => {
                if (!this.active) return;
                this.lastCoords = [e.clientX, e.clientY];
                this.pointers.set(e.pointerId, map(element, this.getScale(), e.clientX, e.clientY));
                this.moves = [this.moves[0] + e.movementX, this.moves[1] + e.movementY];
            });
        }

        getScale() {
            return this.scale;
        }

        updateScale(scale) {
            this.scale = scale;
        }

        get count() {
            return this.pointers.size;
        }

        get move() {
            return this.moves;
        }

        get coords() {
            return this.pointers.size > 0
                ? Array.from(this.pointers.values()).flat()
                : [0, 0];
        }

        get first() {
            return this.pointers.values().next().value || this.lastCoords;
        }
    }

    const resize = () => {
        if (!canvasRef.current) return;

        const canvas = canvasRef.current;
        const dpr = Math.max(1, 0.5 * window.devicePixelRatio);

        canvas.width = window.innerWidth * dpr;
        canvas.height = window.innerHeight * dpr;

        if (rendererRef.current) {
            rendererRef.current.updateScale(dpr);
        }
    };

    const loop = (now) => {
        if (!rendererRef.current || !pointersRef.current) return;

        rendererRef.current.updateMouse(pointersRef.current.first);
        rendererRef.current.updatePointerCount(pointersRef.current.count);
        rendererRef.current.updatePointerCoords(pointersRef.current.coords);
        rendererRef.current.updateMove(pointersRef.current.move);
        rendererRef.current.render(now);
        animationFrameRef.current = requestAnimationFrame(loop);
    };

    useEffect(() => {
        if (!canvasRef.current) return;

        const canvas = canvasRef.current;
        const dpr = Math.max(1, 0.5 * window.devicePixelRatio);

        rendererRef.current = new WebGLRenderer(canvas, dpr);
        pointersRef.current = new PointerHandler(canvas, dpr);

        rendererRef.current.setup();
        rendererRef.current.init();

        resize();

        if (rendererRef.current.test(defaultShaderSource) === null) {
            rendererRef.current.updateShader(defaultShaderSource);
        }

        loop(0);

        window.addEventListener('resize', resize);

        return () => {
            window.removeEventListener('resize', resize);
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
            if (rendererRef.current) {
                rendererRef.current.reset();
            }
        };
    }, []);

    return canvasRef;
};

// Reusable Hero Component
const WebGLHero = ({
    trustBadge,
    headline = { line1: "Boldly Go", line2: "Where No Man Has Gone" },
    subtitle = "Explore strange new worlds with our WebGL background.",
    buttons,
    className = ""
}) => {
    const canvasRef = useShaderBackground();

    return (
        <div className={`relative w-full h-[600px] md:h-[800px] overflow-hidden bg-black ${className}`}>
            <style dangerouslySetInnerHTML={{
                __html: `
        @keyframes fade-in-down {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in-down {
          animation: fade-in-down 0.8s ease-out forwards;
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
          opacity: 0;
        }
        
        .animation-delay-200 {
          animation-delay: 0.2s;
        }
        
        .animation-delay-400 {
          animation-delay: 0.4s;
        }
        
        .animation-delay-600 {
          animation-delay: 0.6s;
        }
        
        .animation-delay-800 {
          animation-delay: 0.8s;
        }
        
        @keyframes gradient-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient-shift 3s ease infinite;
        }
      `}} />

            <canvas
                ref={canvasRef}
                className="absolute inset-0 w-full h-full object-cover touch-none"
                style={{ background: 'black' }}
            />

            {/* Hero Content Overlay */}
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center text-white pointer-events-none p-4">
                {/* Trust Badge */}
                {trustBadge && (
                    <div className="mb-8 animate-fade-in-down pointer-events-auto">
                        <div className="flex items-center gap-2 px-6 py-3 bg-orange-500/10 backdrop-blur-md border border-orange-300/30 rounded-full text-sm">
                            {trustBadge.icons && (
                                <div className="flex">
                                    {trustBadge.icons.map((icon, index) => (
                                        <span key={index} className={`text-${index === 0 ? 'yellow' : index === 1 ? 'orange' : 'amber'}-300`}>
                                            {icon}
                                        </span>
                                    ))}
                                </div>
                            )}
                            <span className="text-orange-100">{trustBadge.text}</span>
                        </div>
                    </div>
                )}

                <div className="text-center space-y-6 max-w-5xl mx-auto px-4 mt-[-50px]">
                    {/* Main Heading with Animation */}
                    <div className="space-y-2">
                        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold bg-gradient-to-r from-orange-300 via-yellow-400 to-amber-300 bg-clip-text text-transparent animate-fade-in-up animation-delay-200 drop-shadow-lg">
                            {headline.line1}
                        </h1>
                        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold bg-gradient-to-r from-yellow-300 via-orange-400 to-red-400 bg-clip-text text-transparent animate-fade-in-up animation-delay-400 drop-shadow-lg">
                            {headline.line2}
                        </h1>
                    </div>

                    {/* Subtitle with Animation */}
                    <div className="max-w-3xl mx-auto animate-fade-in-up animation-delay-600">
                        <p className="text-md md:text-lg lg:text-xl text-orange-100/90 font-light leading-relaxed drop-shadow-md">
                            {subtitle}
                        </p>
                    </div>

                    {/* CTA Buttons with Animation */}
                    {buttons && (
                        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10 animate-fade-in-up animation-delay-800 pointer-events-auto">
                            {buttons.primary && (
                                <button
                                    onClick={buttons.primary.onClick}
                                    className="px-8 py-4 bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-black rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-orange-500/25"
                                >
                                    {buttons.primary.text}
                                </button>
                            )}
                            {buttons.secondary && (
                                <button
                                    onClick={buttons.secondary.onClick}
                                    className="px-8 py-4 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-300/30 hover:border-orange-300/50 text-orange-100 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 backdrop-blur-sm"
                                >
                                    {buttons.secondary.text}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export const defaultShaderSource = `#version 300 es
/*********
* made by Matthias Hurrle (@atzedent)
*
*	To explore strange new worlds, to seek out new life
*	and new civilizations, to boldly go where no man has
*	gone before.
*/
precision highp float;
out vec4 O;
uniform vec2 resolution;
uniform float time;
#define FC gl_FragCoord.xy
#define T time
#define R resolution
#define MN min(R.x,R.y)
// Returns a pseudo random number for a given point (white noise)
float rnd(vec2 p) {
  p=fract(p*vec2(12.9898,78.233));
  p+=dot(p,p+34.56);
  return fract(p.x*p.y);
}
// Returns a pseudo random number for a given point (value noise)
float noise(in vec2 p) {
  vec2 i=floor(p), f=fract(p), u=f*f*(3.-2.*f);
  float
  a=rnd(i),
  b=rnd(i+vec2(1,0)),
  c=rnd(i+vec2(0,1)),
  d=rnd(i+1.);
  return mix(mix(a,b,u.x),mix(c,d,u.x),u.y);
}
// Returns a pseudo random number for a given point (fractal noise)
float fbm(vec2 p) {
  float t=.0, a=1.; mat2 m=mat2(1.,-.5,.2,1.2);
  for (int i=0; i<5; i++) {
    t+=a*noise(p);
    p*=2.*m;
    a*=.5;
  }
  return t;
}
float clouds(vec2 p) {
	float d=1., t=.0;
	for (float i=.0; i<3.; i++) {
		float a=d*fbm(i*10.+p.x*.2+.2*(1.+i)*p.y+d+i*i+p);
		t=mix(t,d,a);
		d=a;
		p*=2./(i+1.);
	}
	return t;
}
void main(void) {
	vec2 uv=(FC-.5*R)/MN,st=uv*vec2(2,1);
	vec3 col=vec3(0);
	float bg=clouds(vec2(st.x+T*.5,-st.y));
	uv*=1.-.3*(sin(T*.2)*.5+.5);
	for (float i=1.; i<12.; i++) {
		uv+=.1*cos(i*vec2(.1+.01*i, .8)+i*i+T*.5+.1*uv.x);
		vec2 p=uv;
		float d=length(p);
		col+=.00125/d*(cos(sin(i)*vec3(1,2,3))+1.);
		float b=noise(i+p+bg*1.731);
		col+=.002*b/length(max(p,vec2(b*p.x*.02,p.y)));
		col=mix(col,vec3(bg*.25,bg*.137,bg*.05),d);
	}
	O=vec4(col,1);
}`;

export default WebGLHero;
